import { mapState, mapStores } from 'pinia';
import { productPurchaseAvailabilities, orderPaymentStatuses, productOptionTypes } from '@/Models';
import { getEffectivePurchaseLimit } from '@/Utils/ProductUtility';
import { useCartSummaryStore } from '@/Stores/Web/cartSummaryStore';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';
import { useProjectUserContextStore } from '@/Stores/Web/projectUserContextStore';
import { useUserContextStore } from '@/Stores/Web/userContextStore';

export default {
    props: {
        tooltipText: { default: null, type: String },
        purchaseLimit: { default: null, type: Number },
    },
    data() {
        return {
            model: {
                quantity: this.quantity,
            },
            productData: null,
        };
    },
    computed: {
        effectivePurchaseLimit() {
            const projectVersion = this.projectContextStore.projectContext.project.version;
            return getEffectivePurchaseLimit(this.effectivePurchaseLimitModel, this.getAvailableQuantityFromPledge, projectVersion, this.isAuthenticated);
        },
        effectivePurchaseLimitModel() {
            const hasStockLimits = this.stockLimits && Object.keys(this.stockLimits).length > 0;
            if (hasStockLimits) {
                const { remainingStockLimit, remainingPerUserLimit } = this.stockLimits;
                return {
                    productID: this.productId,
                    remainingStockLimit,
                    remainingPerUserLimit,
                };
            }

            if (this.productData) {
                const { productID, remainingStockLimit, remainingPerUserLimit } = this.productData;
                return { productID, remainingStockLimit, remainingPerUserLimit };
            }

            return {
                productID: this.productId,
                remainingStockLimit: this.purchaseLimit,
                remainingPerUserLimit: null,
            };
        },
        isDisabled() {
            return this.$validator.errors.any() || this.readOnly;
        },
        hasUnselectedOptions() {
            const isOptionUnset = o => o.optionType === productOptionTypes.customText
                ? !o.valueText
                : !o.productOptionValueID;

            if (this.productData?.productSetItems?.length) {
                return this.productData.productSetItems.some(item => item.setItem.options?.some(isOptionUnset));
            }
            return this.productData?.options?.some(isOptionUnset) ?? false;
        },
        limitReachedTooltip() {
            return this.projectContextStore.isProjectRetailType
                ? this.Resources.Product.StoreProductValidationLimitReachedTooltipText
                : this.Resources.Product.ProductValidationLimitReachedTooltipText;
        },
        stockLimitReachedTooltip() {
            return this.projectContextStore.isProjectRetailType
                ? this.Resources.Product.StoreProductValidationStockLimitReachedTooltipText
                : this.Resources.Product.ProductValidationStockLimitReachedTooltipText;
        },
        tooltipContent() {
            const purchaseLimit = this.effectivePurchaseLimit;

            if (this.model.quantity < 1) {
                return this.Resources.Product.ProductValidationMinQuantityTooltipText;
            }

            if (this.hasUnselectedOptions && this.model.quantity <= purchaseLimit.value) {
                return this.Resources.Product.ProductValidationUnselectedOptionsTooltipText;
            }

            if (this.hasInvalidSplits()) {
                return this.Resources.Product.SplitItemInvalidAmountTooltipText;
            }

            if (this.tooltipText) {
                return this.tooltipText;
            }

            if (this.projectUserContext.hasOrderWithPaymentAction) {
                if (this.orderWithPaymentAction?.cart?.orderPaymentStatus === orderPaymentStatuses.processingPayment) {
                    return this.Resources.Product.ProductValidationPaymentProcessingTooltipText;
                }
                return this.projectContextStore.isProjectRetailType
                    ? this.Resources.Product.ProductValidationOrderNotFinishedTooltipText
                    : this.Resources.Product.ProductValidationPledgeNotFinishedTooltipText;
            }

            if (this.projectUserContext.hasLockedActiveOrder) {
                return this.Resources.ProjectHome.LockedOrderTooltip;
            }

            if (purchaseLimit.status === productPurchaseAvailabilities.stockLimitReached)
                return this.stockLimitReachedTooltip;

            if (purchaseLimit.status === productPurchaseAvailabilities.perUserLimitReached)
                return this.limitReachedTooltip;

            if (purchaseLimit.status === productPurchaseAvailabilities.availableOnlyForLoggedInUsers)
                return this.Resources.Product.ItemLimitedToLoggedInUsersTooltip;

            if (!this.areProductOptionsValid && this.model.quantity <= purchaseLimit.value) {
                // If product options are invalid, we don't want to show any tooltip, because fields are already highlighted with error messages.
                return;
            }

            if (purchaseLimit.value !== null) {
                const pluralizedCount = this.Resources.pluralize(purchaseLimit.value, this.Resources.Product.ProductLimitPluralizedCount);
                return this.Resources.format(this.Resources.Product.ProductValidationLimitInfoTooltipText, pluralizedCount);
            }

            return this.limitReachedTooltip;
        },
        ...mapState(useProjectUserContextStore, ['projectUserContext', 'orderWithPaymentAction']),
        ...mapState(useCartSummaryStore, ['getAvailableQuantityFromPledge']),
        ...mapState(useUserContextStore, ['isAuthenticated']),
        ...mapStores(useProjectContextStore),
    },
    methods: {
        hasInvalidSplits() {
            return false;
        },
    },
};
